import React, { useState, useContext, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import toast from "react-hot-toast";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";

import SuiBadge from "components/SuiBadge";

import { AccountContext } from "context/account";
import api from "services/api";

import CreateResponseTemplatePrompt from "pages/setting/components/CreateResponseTemplatePrompt";
import Prompt from "components/UpStyled/Prompt";

import { nanoid } from "nanoid";

function ProjectSetting() {
  const { id, metadata, handleUpdate } = useContext(AccountContext);
  const [createTemplateVisible, setCreateTemplateVisible] = useState(false);
  const [editTemplateVisible, setEditTemplateVisible] = useState(false);
  const [deleteTemplateVisible, setDeleteTemplateVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState();
  const handleDeleteTemplate = () => {
    if (!metadata["project_response_template"]) {
      metadata["project_response_template"] = [];
    }
    let newMeta = { ...metadata };
    newMeta["project_response_template"] = metadata["project_response_template"].filter(
      (x) => x.id != selectedTemplate.id
    );
    handleUpdate(id, { metadata: newMeta });
    setDeleteTemplateVisible(false);
  };

  const handleCreateTemplate = (data) => {
    if (!metadata["project_response_template"]) {
      metadata["project_response_template"] = [];
    }
    let newMeta = { ...metadata };
    let temp = {
      id: nanoid(),
      title: data["title"],
      text: data["text"],
    };
    newMeta["project_response_template"].push(temp);
    if (newMeta["project_response_template"].length == 1) {
      newMeta["project_response_template"]["default"] = true;
    }

    handleUpdate(id, { metadata: newMeta });
    setCreateTemplateVisible(false);
  };

  const handleEditTemplate = (data) => {
    if (!metadata["project_response_template"]) {
      metadata["project_response_template"] = [];
    }
    let newMeta = { ...metadata };
    let findIndex = newMeta["project_response_template"].findIndex(
      (x) => x.id == selectedTemplate.id
    );

    (newMeta["project_response_template"][findIndex]["title"] = data["title"]),
      (newMeta["project_response_template"][findIndex]["text"] = data["text"]),
      handleUpdate(id, { metadata: newMeta });
    setEditTemplateVisible(false);
  };

  const handleSetDefault = (temp) => {
    if (!metadata["project_response_template"]) {
      metadata["project_response_template"] = [];
    }
    let newMeta = { ...metadata };

    for (let i = 0; i < newMeta["project_response_template"].length; i++) {
      if (newMeta["project_response_template"][i]["id"] == temp.id) {
        newMeta["project_response_template"][i]["default"] = true;
      } else {
        newMeta["project_response_template"][i]["default"] = false;
      }
    }
    handleUpdate(id, { metadata: newMeta });
    setEditTemplateVisible(false);
  };

  return (
    <Card id="project-settings">
      <SuiBox px={3} pt={3} pb={1}>
        <SuiTypography variant="h5">Project Settings</SuiTypography>
      </SuiBox>
      <SuiBox pb={3} px={3}>
        <SuiBox mb={1}>
          <SuiTypography variant="h6" color="text">
            Quick Response Templates
          </SuiTypography>
        </SuiBox>
        <List>
          {metadata?.project_response_template && metadata?.project_response_template.length > 0
            ? metadata?.project_response_template.map((x) => (
                <ListItem
                  disablePadding
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => {
                        setSelectedTemplate(x);
                        setDeleteTemplateVisible(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemButton
                    onClick={() => {
                      setSelectedTemplate(x);
                      setEditTemplateVisible(true);
                    }}
                  >
                    <ListItemText
                      primary={
                        <SuiBox>
                          {x.title}{" "}
                          <SuiBox component="span" ml={1}>
                            {x.default == true ? (
                              <SuiButton
                                variant="text"
                                color={"dark"}
                                size="small"
                                sx={{
                                  fontSize: 12,
                                  lineHeight: 0,
                                  minHeight: 24,
                                  textTransform: "capitalize",
                                  borderRadius: "4px",
                                  padding: "0px 6px",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                Default{" "}
                                <CheckIcon
                                  color="success"
                                  sx={{ width: 16, height: 16, ml: 0.5 }}
                                />
                              </SuiButton>
                            ) : (
                              <SuiButton
                                variant="contained"
                                color={"text"}
                                size="small"
                                sx={{
                                  fontSize: 10,
                                  lineHeight: 0,
                                  minHeight: 24,
                                  textTransform: "capitalize",
                                  borderRadius: "4px",
                                  padding: "0px 6px",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSetDefault(x);
                                }}
                              >
                                Set As Default
                              </SuiButton>
                            )}
                          </SuiBox>{" "}
                        </SuiBox>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))
            : null}
          <ListItem disablePadding>
            <ListItemButton onClick={() => setCreateTemplateVisible(true)}>
              <ListItemText primary="+ New Template" />
            </ListItemButton>
          </ListItem>
        </List>
      </SuiBox>

      {/**Delete template */}
      {selectedTemplate?.id && deleteTemplateVisible ? (
        <Prompt
          heading="Delete Template"
          text={
            <SuiTypography variant="body2" sx={{ wordBreak: "break-all" }}>
              Are you sure you want to delete the template: {selectedTemplate.title}
            </SuiTypography>
          }
          visible={deleteTemplateVisible}
          okText="Delete"
          okColor="error"
          handleOk={() => handleDeleteTemplate(selectedTemplate.id)}
          handleClose={() => setDeleteTemplateVisible(false)}
        />
      ) : null}
      {/**create template */}
      {createTemplateVisible ? (
        <CreateResponseTemplatePrompt
          visible={createTemplateVisible}
          handleClose={() => setCreateTemplateVisible(false)}
          handleOk={handleCreateTemplate}
        />
      ) : null}
      {/**edit template */}
      {editTemplateVisible && selectedTemplate ? (
        <CreateResponseTemplatePrompt
          visible={editTemplateVisible}
          handleClose={() => setEditTemplateVisible(false)}
          handleOk={handleEditTemplate}
          isEdit={true}
          template={selectedTemplate}
        />
      ) : null}
    </Card>
  );
}

export default ProjectSetting;
